import React, { useEffect, useState } from "react";
import { useInterval } from "usehooks-ts";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { query, collection, getDocs, where } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { FirebaseService } from "../../services/firebase.service";
import { WalletService } from "../../services/wallet.service";
import { utils } from "ethers";
import "./Wallet.scss";
import Button from "../../components/Button";
import Loader from "../../components/Loader";
import Footer from "../../components/Footer";

const goerliBlockExplorerUrl = "https://goerli.etherscan.io/";
const POLLING_DELAY_MS = 10000;

export const Wallet = () => {
  const [user, loading] = useAuthState(auth);
  const [name, setName] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [walletAddress, setWalletAddress] = useState("");
  const [userTokenBalance, setUserTokenBalance] = useState("");
  const [targetAddress, setTargetAddress] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [txHash, setTxHash] = useState("");
  const navigate = useNavigate();

  const fetchUserTokenBalance = async (address: string) => {
    const userTokenBalance = await WalletService.getTokenBalancePerAddress(
      address
    );
    setUserTokenBalance(userTokenBalance!);
  };

  const fetchUserName = async () => {
    try {
      const q = query(collection(db, "users"), where("uid", "==", user?.uid));
      const doc = await getDocs(q);
      const data = doc.docs[0].data();
      setName(data.name);
      setWalletAddress(data.walletAddress);
      await fetchUserTokenBalance(data.walletAddress);
    } catch (err) {
      console.error(err);
      alert("An error occured while fetching user data");
    }
  };

  const transferTokens = async () => {
    try {
      if (targetAddress && transferAmount) {
        setIsLoading(true);
        const transferResponse = await WalletService.requestTokenTransfer(
          targetAddress,
          +transferAmount
        );
        setTxHash(transferResponse?.data?.transaction?.hash);
        setIsLoading(false);
      } else {
        alert("Please fill target address and amount");
      }
    } catch (e) {
      console.log(e);
      setIsLoading(false);
    }
  };

  const handleResize = () => {
    if (window.innerWidth <= 1200) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
  });

  useInterval(
    async () => {
      if (walletAddress) {
        await fetchUserTokenBalance(walletAddress);
      }
    },
    // Delay in milliseconds or null to stop it
    POLLING_DELAY_MS
  );

  useEffect(() => {
    if (loading) return;
    if (!user) return navigate("/");

    fetchUserName();
  }, [user, loading]);

  const validateAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value;
    const isAddress = utils.isAddress(address);

    if (isAddress) {
      setTargetAddress(address);
      e.target.setCustomValidity("");
      document.getElementById("target-address")?.classList.remove("invalid");
    } else {
      setTargetAddress("");
      e.target.setCustomValidity("Invalid Address");
      document.getElementById("target-address")?.classList.add("invalid");
    }
  };

  return (
    <div
      className={`row wallet-page page-wrapper ${
        !isMobile ? "desktop-background-image" : ""
      }`}
    >
      <div className="col m6 section-wrapper">
        <div className="widget-title">Wallet</div>
        <div className="wallet-form">
          {!walletAddress || isLoading ? (
            <div className="loader">
              <Loader />
            </div>
          ) : (
            <div className="wallet-info">
              <div className="wallet-address">{walletAddress}</div>
              <div>{name} currently has</div>
              <div className="text-title-bold">{userTokenBalance}</div>
              <div className="points-label text-header-bold">xID Tokens</div>
            </div>
          )}
          {!txHash && (
            <div>
              <div className="auth-input-field input-field">
                <input
                  id="target-address"
                  type="text"
                  className="validate"
                  onChange={validateAddress}
                  required
                />
                <i className="material-icons icon">check</i>
                <label htmlFor="target-address">Target address</label>
                <span
                  className="helper-text"
                  data-error="Invalid Address"
                ></span>
              </div>
              <div className="auth-input-field input-field">
                <input
                  id="transfer-amount"
                  type="number"
                  step="any"
                  className="validate"
                  onChange={(event) => setTransferAmount(event.target.value)}
                  required
                />
                <i className="material-icons icon">check</i>
                <label htmlFor="transfer-amount">Amount</label>
                <span className="helper-text" data-error="Required"></span>
              </div>
            </div>
          )}
          {txHash && (
            <div className="tx-hash">
              <a
                href={`${goerliBlockExplorerUrl}tx/${txHash}`}
                target="_blank"
                rel="noreferrer"
              >
                Transaction Details
              </a>
            </div>
          )}
          <div className="send-button-container">
            <Button
              type="primary"
              size="large"
              disabled={!targetAddress || !transferAmount}
              onClick={transferTokens}
            >
              Send
            </Button>
          </div>
          <div>
            <Button
              type="secondary"
              size="large"
              onClick={FirebaseService.logout}
            >
              Logout
            </Button>
          </div>
        </div>
      </div>
      {!isMobile && (
        <div className="col m6">
          <Footer />
        </div>
      )}
    </div>
  );
};
